import { Link } from "react-router-dom";
import { Shield, FlaskConical, Award, Truck, ArrowRight, Beaker, Atom } from "lucide-react";
import ProductCard from "@/components/ProductCard";
import { PRODUCTS } from "@/data/siteData";

export default function Landing() {
  const featured = PRODUCTS.slice(0, 4);

  return (
    <div className="grid-bg">
      {/* Hero */}
      <section
        data-testid="hero-section"
        className="relative hero-overlay min-h-[85vh] flex items-center scanlines overflow-hidden"
        style={{
          backgroundImage: `url(https://images.unsplash.com/photo-1737629918399-45526c4f9264?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxuZW9uJTIwYmx1ZSUyMG1hZ2VudGElMjBhYnN0cmFjdHxlbnwwfHx8fDE3NzY5MDYyNTF8MA&ixlib=rb-4.1.0&q=85)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="max-w-3xl">
            <div
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-8 fade-in-up"
              style={{ border: '1px solid rgba(0,240,255,0.2)', background: 'rgba(0,240,255,0.05)' }}
            >
              <Shield className="w-3.5 h-3.5" style={{ color: 'var(--neon-cyan)' }} />
              <span className="text-[11px] tracking-[0.2em] uppercase" style={{ color: 'var(--neon-cyan)', fontFamily: "'Roboto Mono', monospace" }}>
                US-Based Research Supplier
              </span>
            </div>

            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter mb-6 fade-in-up fade-in-up-delay-1"
              style={{ fontFamily: "'Rajdhani', sans-serif", lineHeight: 1.05 }}
            >
              Precision-Grade{" "}
              <span className="neon-text-cyan">Research</span>{" "}
              Peptides &{" "}
              <span className="neon-text-magenta">Compounds</span>
            </h1>

            <p
              className="text-base md:text-lg leading-relaxed mb-10 fade-in-up fade-in-up-delay-2 max-w-2xl"
              style={{ color: 'var(--text-secondary)' }}
            >
              Apollo Compounds delivers premium-purity research peptides to laboratories and institutions nationwide. Every compound verified to &gt;99% purity via HPLC and mass spectrometry analysis.
            </p>

            <div className="flex flex-wrap gap-4 fade-in-up fade-in-up-delay-3">
              <Link to="/products" data-testid="hero-browse-btn" className="btn-neon px-8 py-3 rounded text-sm inline-flex items-center gap-2">
                BROWSE CATALOG <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/contact" data-testid="hero-contact-btn" className="btn-ghost-neon px-8 py-3 rounded text-sm">
                CONTACT US
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <section style={{ background: 'var(--bg-surface)', borderTop: '1px solid rgba(0,240,255,0.08)', borderBottom: '1px solid rgba(0,240,255,0.08)' }}>
        <div className="max-w-7xl mx-auto px-6 py-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { icon: FlaskConical, label: "Compounds", value: "7+" },
              { icon: Award, label: "Purity", value: ">99%" },
              { icon: Beaker, label: "HPLC Verified", value: "100%" },
              { icon: Truck, label: "US Shipping", value: "Fast" },
            ].map(({ icon: Icon, label, value }) => (
              <div key={label} className="text-center">
                <Icon className="w-5 h-5 mx-auto mb-2" style={{ color: 'var(--neon-cyan)' }} />
                <div className="text-2xl font-bold" style={{ fontFamily: "'Rajdhani', sans-serif" }}>{value}</div>
                <div className="text-[11px] tracking-[0.15em] uppercase" style={{ color: 'var(--text-secondary)', fontFamily: "'Roboto Mono', monospace" }}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section data-testid="featured-products" className="max-w-7xl mx-auto px-6 py-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <span className="text-[11px] tracking-[0.2em] uppercase block mb-2" style={{ color: 'var(--neon-magenta)', fontFamily: "'Roboto Mono', monospace" }}>
              Research Catalog
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
              Featured Compounds
            </h2>
          </div>
          <Link to="/products" data-testid="view-all-products-btn" className="hidden md:inline-flex items-center gap-2 text-sm transition-colors duration-300" style={{ color: 'var(--neon-cyan)', fontFamily: "'Rajdhani', sans-serif", fontWeight: 600 }}>
            VIEW ALL <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {featured.map((product, i) => (
            <ProductCard key={product.slug} product={product} index={i} />
          ))}
        </div>

        <div className="md:hidden text-center mt-8">
          <Link to="/products" className="btn-ghost-neon px-8 py-2.5 rounded text-sm inline-flex items-center gap-2">
            VIEW ALL PRODUCTS <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Why Apollo */}
      <section style={{ background: 'var(--bg-surface)' }}>
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="text-center mb-16">
            <span className="text-[11px] tracking-[0.2em] uppercase block mb-2" style={{ color: 'var(--neon-magenta)', fontFamily: "'Roboto Mono', monospace" }}>
              Why Choose Us
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
              The Apollo Standard
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Award, title: "Verified Purity", desc: "Every batch undergoes rigorous HPLC and mass spectrometry analysis to ensure >99% purity. Certificates of Analysis available for all products." },
              { icon: Atom, title: "Research-Grade Quality", desc: "Our compounds are synthesized using industry-leading solid-phase peptide synthesis (SPPS) methods, meeting the highest standards for laboratory research." },
              { icon: Truck, title: "US-Based Operations", desc: "Headquartered in the United States with domestic shipping for fast, reliable delivery. Proper cold-chain handling ensures compound integrity." },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="neon-border rounded-lg p-8 card-hover" style={{ background: 'var(--bg-primary)' }}>
                <Icon className="w-8 h-8 mb-4" style={{ color: 'var(--neon-cyan)' }} />
                <h3 className="text-lg font-bold mb-3" style={{ fontFamily: "'Rajdhani', sans-serif" }}>{title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="neon-border rounded-lg p-12 md:p-16 text-center relative overflow-hidden" style={{ background: 'var(--bg-surface)' }}>
          <div className="absolute inset-0 opacity-5" style={{ background: 'radial-gradient(circle at 50% 50%, var(--neon-cyan), transparent 70%)' }} />
          <div className="relative">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
              Ready to Advance Your <span className="neon-text-cyan">Research</span>?
            </h2>
            <p className="text-sm mb-8 max-w-xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
              Contact our team to discuss your research requirements, request bulk pricing, or inquire about specific compounds.
            </p>
            <div className="flex justify-center gap-4 flex-wrap">
              <Link to="/contact" data-testid="cta-inquire-btn" className="btn-neon px-8 py-3 rounded text-sm inline-flex items-center gap-2">
                CONTACT US <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/products" className="btn-ghost-neon px-8 py-3 rounded text-sm">
                VIEW CATALOG
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
